import { logger } from './logger';

export interface ErrorContext {
  operation?: string;
  file?: string;
  user?: string;
  metadata?: Record<string, any>;
}

export class VibecodeError extends Error {
  public code: string;
  public statusCode: number;
  public context?: ErrorContext;
  public isOperational: boolean;

  constructor(
    message: string,
    code: string = 'UNKNOWN_ERROR',
    statusCode: number = 500,
    isOperational: boolean = true,
    context?: ErrorContext
  ) {
    super(message);
    this.name = 'VibecodeError';
    this.code = code;
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    this.context = context;

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

export class ValidationError extends VibecodeError {
  constructor(message: string, context?: ErrorContext) {
    super(message, 'VALIDATION_ERROR', 400, true, context);
    this.name = 'ValidationError';
  }
}

export class AuthorizationError extends VibecodeError {
  constructor(message: string, context?: ErrorContext) {
    super(message, 'AUTHORIZATION_ERROR', 403, true, context);
    this.name = 'AuthorizationError';
  }
}

export class NotFoundError extends VibecodeError {
  constructor(message: string, context?: ErrorContext) {
    super(message, 'NOT_FOUND', 404, true, context);
    this.name = 'NotFoundError';
  }
}

export class RateLimitError extends VibecodeError {
  public retryAfter: number;

  constructor(message: string, retryAfter: number, context?: ErrorContext) {
    super(message, 'RATE_LIMIT_EXCEEDED', 429, true, context);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

export class ErrorHandler {
  private errorHandlers: Map<string, (error: VibecodeError) => void> = new Map();

  /**
   * Register a custom error handler for specific error codes
   */
  registerHandler(errorCode: string, handler: (error: VibecodeError) => void): void {
    this.errorHandlers.set(errorCode, handler);
  }

  /**
   * Handle an error
   */
  handle(error: Error | VibecodeError, context?: ErrorContext): {
    message: string;
    code: string;
    statusCode: number;
    details?: any;
  } {
    // Convert to VibecodeError if needed
    const vibecodeError = this.toVibecodeError(error, context);

    // Log the error
    this.logError(vibecodeError);

    // Call custom handler if registered
    const customHandler = this.errorHandlers.get(vibecodeError.code);
    if (customHandler) {
      customHandler(vibecodeError);
    }

    // Return error response
    return this.createErrorResponse(vibecodeError);
  }

  /**
   * Convert any error to VibecodeError
   */
  private toVibecodeError(error: Error | VibecodeError, context?: ErrorContext): VibecodeError {
    if (error instanceof VibecodeError) {
      // Merge context if provided
      if (context) {
        error.context = { ...error.context, ...context };
      }
      return error;
    }

    // Handle specific error types
    if (error.name === 'ValidationError') {
      return new ValidationError(error.message, context);
    }

    if (error.message.includes('ENOENT')) {
      return new NotFoundError(`File not found: ${error.message}`, context);
    }

    if (error.message.includes('EACCES')) {
      return new AuthorizationError(`Permission denied: ${error.message}`, context);
    }

    // Default error
    return new VibecodeError(
      error.message,
      'INTERNAL_ERROR',
      500,
      false,
      context
    );
  }

  /**
   * Log error with appropriate level
   */
  private logError(error: VibecodeError): void {
    const logContext = {
      code: error.code,
      statusCode: error.statusCode,
      isOperational: error.isOperational,
      context: error.context,
      stack: error.stack,
    };

    if (error.isOperational) {
      logger.warn(`Operational error: ${error.message}`, logContext);
    } else {
      logger.error(`System error: ${error.message}`, error);
    }
  }

  /**
   * Create error response
   */
  private createErrorResponse(error: VibecodeError): {
    message: string;
    code: string;
    statusCode: number;
    details?: any;
  } {
    const response: any = {
      message: error.message,
      code: error.code,
      statusCode: error.statusCode,
    };

    // Add additional details for specific error types
    if (error instanceof RateLimitError) {
      response.details = {
        retryAfter: error.retryAfter,
      };
    }

    // Add context in development
    if (process.env.NODE_ENV === 'development' && error.context) {
      response.details = {
        ...response.details,
        context: error.context,
      };
    }

    return response;
  }

  /**
   * Wrap an async function with error handling
   */
  wrapAsync<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    context?: ErrorContext
  ): T {
    return (async (...args: Parameters<T>) => {
      try {
        return await fn(...args);
      } catch (error) {
        throw this.handle(error as Error, context);
      }
    }) as T;
  }

  /**
   * Create a middleware for Express-like frameworks
   */
  middleware() {
    return (err: Error, req: any, res: any, next: any) => {
      const context: ErrorContext = {
        operation: `${req.method} ${req.path}`,
        user: req.user?.id,
        metadata: {
          ip: req.ip,
          userAgent: req.get('user-agent'),
        },
      };

      const errorResponse = this.handle(err, context);

      res.status(errorResponse.statusCode).json({
        error: errorResponse.message,
        code: errorResponse.code,
        ...errorResponse.details,
      });
    };
  }
}

// Default error handler instance
export const errorHandler = new ErrorHandler();